/**
 * Personal home ("Your NBA"): greeting by your team, who you follow, the one game that matters
 * with ONE action (Catch me up · 60 sec), a language toggle, a single optional onboarding
 * question, and "Your story so far" from your own history. Hidden without the director API.
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { TEAMS, team, directorTeamName, type DirectorLanguage, type DirectorProfile, type Fan, type LiveGameContext } from '@clutch/core';
import { useClient } from '../client';
import { navigate } from '../router';
import { teamAccent } from '../ui/color';
import { TeamLogo } from '../ui/Media';
import type { DirectorHistory, OnboardingQuestion } from '../director/api';
import { historyCards, type HistoryCard } from '../director/history';
import { greeting, strings } from '../director/strings';
import { LangToggle } from '../director/LangToggle';
import { htmlLang, pick } from '../director/i18n';
import type { FeedCard } from './feed';
import { latestGame, type LatestGame } from './personal';
import { cardHeadline, cardStatus, cardDate, weekCopy, suggestedProfile } from './language';
import { preferenceCopy } from './preference-copy';
import { defaultProfile, interests, parsePlayers, ProfileWriter } from './preferences';

const COPY = {
  en: { mattered: (c?: string) => (c ? `Here’s what mattered to ${c}.` : 'Here’s what mattered tonight.'), following: 'Following', edit: 'Edit', yourStory: 'Your story so far', none: 'Make a call in any game and your story starts here.', skip: 'Skip', live: 'Live now', final: 'Latest finish', classic: 'Classic finish', featured: 'Start here', history: 'Pick up where you left off', opening: 'Opening…', watched: 'Seen', youCalled: 'You called it', youMissed: 'Missed call', q: { interest: 'Who do you follow?', language: 'How would you like to hear your story?', familiarity: 'How much basketball context would help?' } as Record<string, string>, fam: { new: 'Explain the game', regular: 'Just the key context', expert: 'Keep it concise' } as Record<string, string>, oneQ: 'One question · skippable' },
  hi: { mattered: (c?: string) => (c ? `${c} के लिए आज क्या अहम रहा।` : 'आज रात क्या अहम रहा।'), following: 'फ़ॉलो कर रहे हैं', edit: 'बदलें', yourStory: 'अब तक आपकी कहानी', none: 'किसी भी मैच में कॉल लगाइए — आपकी कहानी यहीं से शुरू होगी।', skip: 'छोड़ें', live: 'अभी लाइव', final: 'ताज़ा नतीजा', classic: 'क्लासिक फ़िनिश', featured: 'यहाँ से शुरू करें', history: 'जहाँ छोड़ा था, वहीं से', opening: 'खुल रहा है…', watched: 'देखा', youCalled: 'सही कॉल', youMissed: 'चूकी कॉल', q: { interest: 'आप किसे फ़ॉलो करते हैं?', language: 'अपनी कहानी किस भाषा में सुनना चाहेंगे?', familiarity: 'बास्केटबॉल का कितना संदर्भ चाहिए?' } as Record<string, string>, fam: { new: 'खेल समझाइए', regular: 'सिर्फ़ ज़रूरी बातें', expert: 'छोटा रखिए' } as Record<string, string>, oneQ: 'एक सवाल · छोड़ सकते हैं' },
  'zh-Hans': { mattered: (c?: string) => c ? `${c}，今天值得关注的故事。` : '今晚值得关注的故事。', following: '关注', edit: '修改', yourStory: '你的观赛故事', none: '在任意比赛中做出预测，你的故事就从这里开始。', skip: '跳过', live: '正在进行', final: '最新收官', classic: '经典收官', featured: '从这里开始', history: '继续上次的故事', opening: '正在打开…', watched: '已看过', youCalled: '你预测对了', youMissed: '预测未中', q: { interest: '你关注谁？', language: '想用哪种语言听故事？', familiarity: '需要多少篮球背景知识？' } as Record<string, string>, fam: { new: '解释比赛规则', regular: '只说关键背景', expert: '简明扼要' } as Record<string, string>, oneQ: '一个问题 · 可以跳过' },
};

export function PersonalHome({ fan, live, classics, scheduleLoaded = true, scheduleUnavailable = false, contexts = [], onEdit, onOpenCard, onProfileChange }: {
  fan: Fan;
  live: readonly FeedCard[];
  classics: readonly FeedCard[];
  scheduleLoaded?: boolean;
  scheduleUnavailable?: boolean;
  contexts?: readonly LiveGameContext[];
  onProfileChange?: (profile: DirectorProfile) => void;
  onEdit: () => void;
  /** Open (create) a room for a feed card; resolves to its roomId. */
  onOpenCard: (c: FeedCard) => Promise<string | undefined>;
}) {
  const client = useClient();
  const api = client.director;
  const [profile, setProfile] = useState<DirectorProfile>();
  const [question, setQuestion] = useState<OnboardingQuestion | null>(null);
  const [history, setHistory] = useState<DirectorHistory>();
  const [busy, setBusy] = useState(false);
  const [answering, setAnswering] = useState(false);
  const [error, setError] = useState<string>();
  const revision = useRef(0);
  const mounted = useRef(false);
  const writer = useMemo(() => api ? new ProfileWriter((patch) => api.saveProfile(fan.fanId, patch)) : undefined, [api, fan.fanId]);

  useEffect(() => {
    if (!api) return;
    mounted.current = true;
    const version = ++revision.current;
    let alive = true;
    api.onboarding(fan.fanId, navigator.language || 'en-US').then((o) => {
      if (!alive || revision.current !== version) return;
      const next = suggestedProfile(o);
      setProfile(next);
      setQuestion(o.nextQuestion?.id === 'interest' ? o.nextQuestion : null);
      // Persist the first-use suggestion once, through the same ordered writer as explicit choices.
      if (o.languageAnswered === false && writer) void writer.write({ language: next.language }).then((saved) => {
        if (alive && revision.current === version) setProfile(saved);
      }).catch(() => { if (alive && revision.current === version) setError(preferenceCopy(next.language).saveError); });
    }).catch(() => undefined);
    api.history(fan.fanId).then((h) => { if (alive) setHistory(h); }).catch(() => undefined);
    return () => { alive = false; mounted.current = false; revision.current++; };
  }, [api, fan.fanId, writer]);
  useEffect(() => { if (profile) onProfileChange?.(profile); }, [profile, onProfileChange]);

  const lang: DirectorLanguage = profile?.language ?? fan.directorProfile?.language ?? 'en';
  const c = COPY[lang];
  const { teams: followed, players } = useMemo(() => interests(profile, fan), [profile, fan]);
  const latest = useMemo(() => latestGame({ followed, players, contexts, live, classics, participation: history?.participation ?? [] }), [followed, players, contexts, live, classics, history]);
  const cards = useMemo(() => historyCards(history, lang), [history, lang]);

  if (!api) return null;

  const answer = async (patch: Partial<DirectorProfile>) => {
    if (answering || !writer) return;
    setAnswering(true);
    setError(undefined);
    const version = ++revision.current;
    try {
      const updated = await writer.write(patch);
      if (mounted.current) { setQuestion(null); if (revision.current === version) setProfile(updated); }
    } catch {
      if (mounted.current && revision.current === version) setError(preferenceCopy(lang).saveError);
    } finally {
      if (mounted.current) setAnswering(false);
    }
  };
  const setLang = (l: DirectorLanguage) => {
    if (!writer) return;
    const version = ++revision.current;
    setError(undefined);
    setProfile((p) => ({ ...(p ?? defaultProfile(fan)), language: l }));
    void writer.write({ language: l }).then((updated) => {
      if (mounted.current && revision.current === version) setProfile(updated);
    }).catch(() => { if (mounted.current && revision.current === version) setError(pick({ en: 'Language wasn’t saved. Select it again to retry.', hi: 'भाषा सेव नहीं हुई। फिर चुनें।', zh: '语言未保存，请重新选择。' }, l)); });
  };

  const catchUp = async (g: LatestGame) => {
    if (busy) return;
    setBusy(true);
    try {
      const roomId = g.kind === 'room' ? g.roomId : await onOpenCard(g.card);
      if (roomId) navigate(g.kind === 'card' && g.card.kind === 'tonight' ? `/room/${encodeURIComponent(roomId)}` : `/room/${encodeURIComponent(roomId)}/catchup?lang=${lang}`);
    } finally {
      setBusy(false);
    }
  };
  const myTeam = followed[0];
  const style = myTeam ? ({ '--mine': teamAccent(myTeam) } as CSSProperties) : undefined;

  return (
    <section className="you" style={style} data-testid="personal-home" lang={htmlLang(lang)}>
      <div className="you__hello">
        <p className="you__time">{greeting(lang)}</p>
        <LangToggle value={lang} onChange={setLang} />
      </div>
      <h1 className="you__title" data-testid="personal-greeting">{c.mattered(myTeam ? lang === 'en' ? team(myTeam).city : directorTeamName(myTeam, lang) : players[0])}</h1>

      <div className="you__follow" aria-label={c.following}>
        {followed.slice(0, 3).map((t) => (
          <span key={t} className="you__team" style={{ '--t': teamAccent(t) } as CSSProperties}><TeamLogo tricode={t} size={26} />{directorTeamName(t, lang)}</span>
        ))}
        {players.slice(0, 2).map((p) => (
          <span key={p} className="you__player"><i aria-hidden="true">{p.split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase()}</i>{p}</span>
        ))}
        <button className="you__edit" onClick={onEdit}>{c.edit}</button>
      </div>

      {question && <QuestionCard q={question} c={c} lang={lang} busy={answering} onAnswer={answer} />}
      {error && <p className="alert" role="alert">{error}</p>}

      {!latest && <p className="you__empty" data-testid="current-games-empty">{scheduleUnavailable ? weekCopy(lang).unavailable : scheduleLoaded ? weekCopy(lang).empty : homeCopyLoading(lang)}</p>}
      {latest && <LatestCard g={latest} c={c} lang={lang} busy={busy} onCatchUp={() => catchUp(latest)} />}

      <div className="you__story" aria-label={c.yourStory}>
        <div className="section-heading"><h2>{c.yourStory}</h2></div>
        {history && cards.length === 0 && <p className="you__empty">{c.none}</p>}
        {!history && <div className="feed__skeleton" aria-busy="true" />}
        <div className="you__cards">{cards.map((h) => <HistoryRow key={h.roomId} h={h} c={c} lang={lang} />)}</div>
      </div>
    </section>
  );
}

function LatestCard({ g, c, lang, busy, onCatchUp }: { g: LatestGame; c: (typeof COPY)['en']; lang: DirectorLanguage; busy: boolean; onCatchUp: () => void }) {
  const t = strings(lang);
  const away = g.kind === 'room' ? g.away : g.card.away.tricode;
  const home = g.kind === 'room' ? g.home : g.card.home.tricode;
  const label = g.reason === 'upcoming' ? weekCopy(lang).upcoming : g.reason === 'live' ? c.live : g.reason === 'history' ? c.history : g.reason === 'final' ? c.final : g.reason === 'classic' ? c.classic : c.featured;
  const headline = g.kind === 'room'
    ? g.result === 'correct' ? c.youCalled : g.result === 'incorrect' ? c.youMissed : `${directorTeamName(away, lang)} · ${directorTeamName(home, lang)}`
    : cardHeadline(g.card, lang);
  const line = g.kind === 'room' ? g.finalScore ?? `${away} @ ${home}` : g.card.kind === 'tonight' ? `${cardDate(g.card, lang)} · ${cardStatus(g.card, lang)}` : `${cardDate(g.card, lang)} · ${away} ${g.card.away.score ?? ''} – ${g.card.home.score ?? ''} ${home} · ${cardStatus(g.card, lang)}`;
  return (
    <article className={`latest latest--${g.reason}`} style={{ '--a': teamAccent(away), '--h': teamAccent(home) } as CSSProperties} data-testid="latest-game" data-game-id={g.kind === 'card' ? g.card.gameId : undefined}>
      <span className="latest__label">{g.reason === 'live' && <i className="feed__live-dot" />}{label}</span>
      <div className="latest__crest" aria-hidden="true"><TeamLogo tricode={away} size={64} /><TeamLogo tricode={home} size={64} /></div>
      <h2 className="latest__headline">{headline}</h2>
      <p className="latest__line">{line}</p>
      <button className="latest__go" onClick={onCatchUp} disabled={busy} data-testid="catch-me-up">
        <span className="latest__play" aria-hidden="true">▶</span>
        <span><b>{busy ? c.opening : g.reason === 'upcoming' ? weekCopy(lang).join : t.catchUp}</b><small>{g.reason === 'upcoming' ? '' : t.sixty}</small></span>
      </button>
    </article>
  );
}

function QuestionCard({ q, c, lang, busy, onAnswer }: { q: OnboardingQuestion; lang: DirectorLanguage; c: (typeof COPY)['en']; busy: boolean; onAnswer: (p: Partial<DirectorProfile>) => void }) {
  const [player, setPlayer] = useState('');
  const [error, setError] = useState<string>();
  const prompt = c.q[q.id] ?? q.prompt;
  return (
    <article className="ask" data-testid="onboarding-question" data-question={q.id}>
      <span className="ask__tag">{c.oneQ}</span>
      <h2 className="ask__q">{prompt}</h2>
      {q.id === 'interest' ? (
        <div className="ask__teams">
          {q.options.filter((o) => TEAMS[o.value]).map((o) => (
            <button key={o.value} disabled={busy} onClick={() => onAnswer({ followedTeams: [o.value] })} aria-label={directorTeamName(o.value, lang)} data-testid="ask-option">
              <TeamLogo tricode={o.value} size={34} /><span>{directorTeamName(o.value, lang)}</span>
            </button>
          ))}
        </div>
      ) : (
        <div className="ask__opts">
          {q.options.map((o) => (
            <button key={o.value} className="ask__opt" disabled={busy} lang={o.value === 'hi' ? 'hi' : undefined} data-testid="ask-option" data-value={o.value}
              onClick={() => onAnswer({ [q.fields[0] ?? q.id]: o.value } as Partial<DirectorProfile>)}>
              {q.id === 'familiarity' ? c.fam[o.value] ?? o.label : o.label}
            </button>
          ))}
        </div>
      )}
      {q.id === 'interest' && <form className="ask__player" onSubmit={(e) => { e.preventDefault(); setError(undefined); try { if (player.trim()) onAnswer({ followedTeams: [], followedPlayers: parsePlayers(player) }); } catch { setError(preferenceCopy(lang).playersError); } }}>
        <label>{pick({ en: 'Or follow a player', hi: 'या किसी खिलाड़ी को फ़ॉलो करें', zh: '也可以关注球员' }, lang)}<input className="preferences__input" maxLength={80} value={player} onChange={(e) => setPlayer(e.target.value)} placeholder="Jalen Brunson" disabled={busy} /></label>
        <button className="btn btn--ghost" type="submit" disabled={busy || !player.trim()}>{pick({ en: 'Follow player', hi: 'फ़ॉलो करें', zh: '关注球员' }, lang)}</button>
      </form>}
      {error && <p className="alert" role="alert">{error}</p>}
      {q.skippable && <button className="ask__skip" disabled={busy} onClick={() => onAnswer(q.skipPatch)} data-testid="ask-skip">{c.skip}</button>}
    </article>
  );
}

function HistoryRow({ h, c, lang }: { h: HistoryCard; c: (typeof COPY)['en']; lang: DirectorLanguage }) {
  const go = () => {
    if (h.action.kind === 'rematch') return document.getElementById('feed-games')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (h.action.kind === 'story' && h.storyPath) return navigate(h.storyPath);
    navigate(`/room/${encodeURIComponent(h.roomId)}/catchup?lang=${lang}`);
  };
  return (
    <article className={`hist hist--${h.kind}`} data-testid="history-card" data-kind={h.kind} data-room-id={h.roomId}>
      <span className="hist__crest" aria-hidden="true"><TeamLogo tricode={h.away} size={28} /><TeamLogo tricode={h.home} size={28} /></span>
      <span className="hist__body">
        <b>{h.title}</b>
        <small>{h.detail}{h.seen ? ` · ${c.watched}` : ''}</small>
      </span>
      <button className={`hist__go${h.action.kind === 'rematch' ? ' is-rematch' : ''}`} onClick={go}>{h.action.label}</button>
    </article>
  );
}

const homeCopyLoading = (lang: DirectorLanguage) => pick({ en: 'Loading current games…', hi: 'वर्तमान मैच लोड हो रहे हैं…', zh: '正在加载当前比赛…' }, lang);
